// @ts-nocheck
import type { TrackingService } from '../models/tracking-service';
import type { Dealer } from '../models/dealer';
import type {
  MapAppSearchResultEnum,
  MapAppSearchResultElementNameEnum,
  searchTypeEnum,
} from '../models/tracking-data';
import {
  appReady,
  impression,
  searchResult,
  locateUser,
  mapAppSearchResult,
  userLocationNotRetrieved,
  dealerDetails,
  emptyState,
} from '../models/tracking-data';

export enum TrackingEvents {
  APP_READY,
  IMPRESSION,
  VIEW_CHANGE,
  SEARCH_RESULT,
  MAP_APP_SEARCH,
  LOCATE_USER,
  LOCATION_NOT_RETRIEVED_PERMISSION_DENIED,
  LOCATION_NOT_RETRIEVED_PERMISSION_GIVEN,
  LOCATION_NOT_RETRIEVED_IP_GEOLOCATION_NOT_RESOLVED,
}

export const trackEventWithParams = (
  trackingService: TrackingService | null,
  event: TrackingEvents,
  params: Record<string, unknown>,
  componentName?: string,
) => {
  if (trackingService == null) {
    return;
  }
  trackingService.featureAppName = 'dealer-search-map-app';
  switch (event) {
    case TrackingEvents.APP_READY:
      trackingService.track({
        ...appReady(componentName),
        ...params,
      });
      break;
    case TrackingEvents.VIEW_CHANGE:
      trackingService.track(
        dealerDetails(
          params.dealerID,
          params.dealerName,
          params.partnerServiceDetails,
          params.matPrimaryCode,
          params.kvpsPartnerKey,
        ),
      );
      break;
    default:
      throw new Error(`Invalid tracking event type: ${event}`);
  }
};

export const trackEvent = (
  trackingService: TrackingService | null,
  event: TrackingEvents,
  featureAppName?: string,
  headline?: string,
  componentName?: string,
) => {
  if (trackingService === null) {
    return;
  }
  trackingService.featureAppName = 'dealer-search-map-app';
  switch (event) {
    case TrackingEvents.IMPRESSION:
      trackingService.track(impression(featureAppName, headline, componentName));
      break;
    case TrackingEvents.LOCATE_USER:
      trackingService.track(locateUser());
      break;
    case TrackingEvents.LOCATION_NOT_RETRIEVED_PERMISSION_GIVEN:
      trackingService.track(
        userLocationNotRetrieved('localization not successful: permission given'),
      );
      break;
    case TrackingEvents.LOCATION_NOT_RETRIEVED_PERMISSION_DENIED:
      trackingService.track(
        userLocationNotRetrieved('localization not successful: permission denied'),
      );
      break;
    case TrackingEvents.LOCATION_NOT_RETRIEVED_IP_GEOLOCATION_NOT_RESOLVED:
      trackingService.track(
        userLocationNotRetrieved(
          'localization not successful: ip geolocation could not be resolved',
        ),
      );
      break;
    default:
      throw new Error(`Invalid tracking event type: ${event}`);
  }
};

export const trackViewChangeEvent = (
  trackingService: TrackingService | null,
  event: TrackingEvents,
  term: string,
  resultsNum: number,
  searchType: searchTypeEnum,
  filters: string[],
  dealers: Dealer[],
  preferredDealer: Dealer | null,
  initialStart: boolean,
) => {
  if (trackingService === null) {
    return;
  }
  trackingService.featureAppName = 'dealer-search-map-app';
  switch (event) {
    case TrackingEvents.VIEW_CHANGE:
      trackingService.track(
        searchResult(term, resultsNum, searchType, filters, dealers, preferredDealer, initialStart),
      );
      break;
    default:
      throw new Error(`Invalid tracking event type: ${event}`);
  }
};

export const trackInitialStateEvent = (
  trackingService: TrackingService | null,
  event: TrackingEvents,
  preferredDealer: Dealer | null,
  initialStart: boolean,
) => {
  if (trackingService === null) {
    return;
  }
  trackingService.featureAppName = 'dealer-search-map-app';
  switch (event) {
    case TrackingEvents.VIEW_CHANGE:
      trackingService.track(emptyState(preferredDealer, initialStart));
      break;
    default:
      throw new Error(`Invalid tracking event type: ${event}`);
  }
};

export const trackInternalLinkEvent = (
  trackingService: TrackingService | null,
  event: TrackingEvents,
  mapAppSearchResultEnum: MapAppSearchResultEnum,
  component: string,
  label: string,
  searchValue: string,
  element: MapAppSearchResultElementNameEnum,
  pos: string | number | undefined,
  targetURL: string,
) => {
  if (trackingService === null) {
    return;
  }
  trackingService.featureAppName = 'dealer-search-map-app';
  switch (event) {
    case TrackingEvents.MAP_APP_SEARCH:
      trackingService.track(
        mapAppSearchResult(
          mapAppSearchResultEnum,
          component,
          label,
          searchValue,
          element,
          pos,
          targetURL,
        ),
      );
      break;
    default:
      throw new Error(`Invalid tracking event type: ${event}`);
  }
};
